import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { ProjectRouteStation } from "../../shared/interfaces/project-route-station.interface";
import { AlertService } from "../../../shared/alert/shared/alert.service";
import { RouteStationMetaData, RouteStationMetaDataTypes } from "../shared/interfaces/route-station-meta.interface";
import { AlertTypes } from "../../../shared/alert/shared/alert-types";
import { RouteStationPositionData, RouteStationPositionDataTypes } from "../shared/interfaces/route-station-position.interface";
import { RouteStationService } from "../../shared/services/route-station.service";
import { MatDialog } from "@angular/material/dialog";
import { DataEndChannelAddModalComponent } from "../data-end-channel-add-modal/data-end-channel-add-modal.component";
import { GroupService } from "../../shared/services/group.service";
import { ProjectGroup } from "../../shared/interfaces/project-group.interface";
import { ProjectDataService } from "../../shared/services/project-data.service";
import { SelectInput } from "../../../shared/dropdown/shared/select-input.class";
import { DataEndChannel } from "../../shared/interfaces/data-end-channel.interface";
import { DataEndChannelTypeEnum } from "../../shared/enums/data-end-channel-type.enum";
import { PositionGmapService } from '../shared/services/position-gmap.service';
import { DataEndChannelError } from "../../shared/interfaces/data-end-channel-error.interface";
import { SchemaService } from '../../project-schema/shared/services/schema.service';
import { ProjectRouteStationWithParam } from '../../shared/interfaces/project-route-station.class';
import { Router } from '@angular/router';
import { DataEndChannelService } from '../../shared/services/data-end-channel.service';
import { Subscription } from 'rxjs';
import { SchemaRouteStationInfo } from '../../project-schema/shared/interfaces/schema-route-station-info.interface';
import { ProjectService } from '../../shared/services/project.service';
import { Project } from '../../shared/interfaces/project.interface';
import { environment } from 'src/environments/environment';
import { MapConsentService } from 'src/app/shared/map-consent/shared/map-consent.service'


@Component({
  selector: 'avs-route-station-settings',
  templateUrl: './route-station-settings.component.html',
  styleUrls: ['./route-station-settings.component.css']
})
export class RouteStationSettingsComponent implements OnDestroy, OnInit, OnChanges {
  @Input() activeRouteStation: ProjectRouteStation | undefined;
  @Input() activeGroup: ProjectGroup | undefined;
  @Input() dataEndChannels: DataEndChannel[] = [];
  @Input() dataEndChannelErrors: DataEndChannelError[] = [];
  @Input() groupsActiveRouteStation: ProjectRouteStationWithParam[] | undefined;
  @Input() activeProjectsOnly: boolean = false;

  @Output() dataEndChannelErrorsRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() dataEndChannelRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stationGroupRelationRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() channelAddedRefresh: EventEmitter<boolean> = new EventEmitter<boolean>();

  public stationMetaData: RouteStationMetaData | undefined;
  private isGoogleMapsEnabled: boolean = environment.googleMapsEnabled;
  public isMapConsentGiven: boolean = false;
  public stationPositionData: RouteStationPositionData | undefined;
  public isGroupDropdownOpen: boolean = false;
  public allProjectGroups: ProjectGroup[] = [];
  public allNotArchivedProjectGroups: ProjectGroup[] = [];
  public routerUrl: string;
  public channelAdded: boolean = false;
  public isSelectedGroupChanged: boolean = false;
  public filteredGroups: ProjectGroup[] = [];
  public loggingChanged: boolean = false;
  protected readonly RouteStationMetaDataTypes = RouteStationMetaDataTypes;
  protected readonly RouteStationPositionDataTypes = RouteStationPositionDataTypes;
  protected readonly SelectInput = SelectInput;
  public schemaInfo: SchemaRouteStationInfo | undefined;

  /** Helligkeitsbereich nur einblenden, wenn die Station mindestens eine LED-Anzeige hat */
  public get isBrightnessSectionVisible(): boolean {
    return this.dataEndChannels.some(
      channel => channel.dataEndChannelType === DataEndChannelTypeEnum.LED);
  }

  /** Wetterkanal der Station; ohne ihn entfällt der Wetterbereich */
  public get weatherChannel(): DataEndChannel | undefined {
    return this.dataEndChannels.find(
      channel => channel.dataEndChannelType === DataEndChannelTypeEnum.WEATHER);
  }

  /**
   * Fuel cell channel of the station; without it the fuel cell section is omitted.
   * After a change of the station the channels are reloaded with a delay, therefore
   * only a channel of the active station counts.
   */
  public get fuelcellChannel(): DataEndChannel | undefined {
    return this.dataEndChannels.find(channel =>
      channel.dataEndChannelType === DataEndChannelTypeEnum.FUELCELL
      && channel.routeStationId === this.activeRouteStation?.id);
  }

  public groupSubscription?: Subscription;
  public projectsSubscription?: Subscription;
  private mapPositionDraftSubscription?: Subscription;
  private mapConsentSubscription?: Subscription;

  public searchInput: string = "";

  allProjects: Project[] = [];


  constructor(private alertService: AlertService,
    private stationService: RouteStationService,
    private dialog: MatDialog,
    private projectDataService: ProjectDataService,
    private groupService: GroupService,
    private positionGmapService: PositionGmapService,
    private schemaService: SchemaService,
    private _router: Router,
    private dataEndChannelService: DataEndChannelService,
    private projectService: ProjectService,
    private mapConsentService: MapConsentService) {
    this.mapConsentSubscription = this.mapConsentService.isConsentGiven.subscribe(isConsentGiven => {
      this.isMapConsentGiven = isConsentGiven && this.isGoogleMapsEnabled;
    });
    this.routerUrl = _router.url;
    this.groupSubscription = projectDataService.activeGroup.subscribe(activeGroup => {
      this.activeGroup = activeGroup;
      this.getAllRouteStations();
    });
    this.projectsSubscription = projectDataService.projects.subscribe(projects => {
      if (projects.length === 0) {
        this.projectService.getProjects().subscribe(fetchedProjects => {
          this.allProjects = fetchedProjects;
          this.filterGroupsNew();
        });
      } else {
        this.allProjects = projects;
        this.filterGroupsNew();
      }

    });
    this.mapPositionDraftSubscription = projectDataService.mapPositionDraft.subscribe(position => {
      if (!position || position.target !== 'routeStation' || !this.activeRouteStation) return;
      this.stationPositionData = { latitude: position.latitude, longitude: position.longitude };
      this.activeRouteStation.latitude = position.latitude;
      this.activeRouteStation.longitude = position.longitude;
    });
  }

  public ngOnInit(): void {
    this.getAllRouteStations();
  }

  public ngOnDestroy(): void {
    this.groupSubscription?.unsubscribe();
    this.projectsSubscription?.unsubscribe();
    this.mapPositionDraftSubscription?.unsubscribe();
    this.mapConsentSubscription?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeRouteStation']) {
      this.getAllRouteStations();
    }
  }

  public onMetaValueChange(newValue: any, type: RouteStationMetaDataTypes): void {
    if (!this.stationMetaData) {
      this.stationMetaData = { name: undefined, version: undefined, telefon: undefined };
    }
    switch (type) {
      case RouteStationMetaDataTypes.name:
        this.stationMetaData.name = newValue;
        break;
      case RouteStationMetaDataTypes.version:
        this.stationMetaData.version = newValue;
        break;
      case RouteStationMetaDataTypes.telefon:
        this.stationMetaData.telefon = newValue;
        break;
    }
  }

  public onGPSDataInProjectClick(): void {
    let gpsData = this.schemaInfo?.gps?.data;
    if (!this.stationPositionData) {
      this.stationPositionData = { latitude: undefined, longitude: undefined };
    }
    if (this.activeRouteStation) {
      this.activeRouteStation.latitude = gpsData?.latitude!
      this.activeRouteStation.longitude = gpsData?.longitude!
    }
    if (this.stationPositionData) {
      this.stationPositionData.latitude = gpsData?.latitude;
      this.stationPositionData.longitude = gpsData?.longitude;
    }
  }

  public onPositionValueChange(newValue: any, type: RouteStationPositionDataTypes): void {
    if (!this.stationPositionData) {
      this.stationPositionData = { latitude: undefined, longitude: undefined };
    }
    switch (type) {
      case RouteStationPositionDataTypes.latitude:
        this.stationPositionData.latitude = newValue;
        break;
      case RouteStationPositionDataTypes.longitude:
        this.stationPositionData.longitude = newValue;
        break;
    }
  }

  public onLoggingChange() {
    this.loggingChanged = true;
  }

  public onSaveMetaDataClicked(): void {
    if (this.activeRouteStation) {
      this.activeRouteStation.name = this.stationMetaData?.name === undefined ?
        this.activeRouteStation.name : this.stationMetaData.name;
      this.activeRouteStation.versionId = this.stationMetaData?.version === undefined ?
        this.activeRouteStation.versionId : this.stationMetaData.version;
      this.activeRouteStation.phoneNo = this.stationMetaData?.telefon === undefined ?
        this.activeRouteStation.phoneNo : this.stationMetaData.telefon;
      this.stationService.editProjectRouteStation(this.activeRouteStation.id, this.activeRouteStation).subscribe(() => {
        this.alertService.alert('Station Metadaten wurden geändert', AlertTypes.info);
        this.stationMetaData = undefined;
      });
    }
  }

  public onSavePositionDataClicked(): void {
    if (this.activeRouteStation) {
      this.activeRouteStation.latitude = this.stationPositionData?.latitude ? this.stationPositionData.latitude : this.activeRouteStation.latitude;
      this.activeRouteStation.longitude = this.stationPositionData?.longitude ? this.stationPositionData.longitude : this.activeRouteStation.longitude;
      this.stationService.editProjectRouteStation(this.activeRouteStation.id, this.activeRouteStation).subscribe(() => {
        this.alertService.alert('Station Positionsdaten wurden geändert', AlertTypes.info);
        this.stationPositionData = undefined;
      });
    }
  }

  public onAddDataEndChannelClicked(): void {
    const dialogRef = this.dialog.open(DataEndChannelAddModalComponent, { data: this.activeRouteStation });
    dialogRef.afterClosed().subscribe(isNewDataEndCannel => {
      if (isNewDataEndCannel) {
        this.dataEndChannelRefresh.emit();
        this.channelAdded = true;
        this.channelAddedRefresh.emit();
      }
    });
  }

/*   public getAllProjectGroups() {
    this.groupService.getAllProjectGroups().subscribe(groupData => { 
      this.allProjectGroups = groupData;
      this.allNotArchivedProjectGroups = groupData.filter(gd => gd.isArchived != true);
      if (this.routerUrl === '/device/settings')
        this.filteredGroups = this.allNotArchivedProjectGroups;
      if (this.routerUrl === '/project/settings')
        this.filteredGroups = this.allProjectGroups;
    });
  } */

  public getAllRouteStations() {

    if (this.groupsActiveRouteStation)
      this.groupsActiveRouteStation.forEach(element => {

        if (this.activeRouteStation) {
          if (element.routeStation.samId == this.activeRouteStation.samId) { this.schemaInfo = element.schemaInfo }
        }
      });

    else {
      if (this.activeRouteStation)
        this.schemaService.getSchemaDeviceInfo(this.activeRouteStation!.id).subscribe(schemaInfo => {
          this.schemaInfo = schemaInfo
        })
    }
  }

  public onGroupDropdownClicked() {
    this.isGroupDropdownOpen = !this.isGroupDropdownOpen;
  }

  public onGroupDropdownValueChange(selectedGroup: any): void {
    this.activeGroup = selectedGroup;
    this.isSelectedGroupChanged = true;
    this.isGroupDropdownOpen = false;
  }

  public onSaveSelectedGroupClicked() {
    this.isSelectedGroupChanged = false;
    this.isGroupDropdownOpen = false;

    if (this.activeRouteStation) {
      if (this.activeGroup) {
        this.activeRouteStation.groupId = +this.activeGroup.id
        this.activeRouteStation.y = 0;
        this.activeRouteStation.x = 0;
        this.stationService.editProjectRouteStation(this.activeRouteStation.id, this.activeRouteStation).subscribe(() => {
          if (this.activeGroup) {
            this.alertService.alert(`Deine Station wurde der Gruppe ${this.activeGroup.name} zugeordnet.`, AlertTypes.success);
            this.projectDataService.updateActiveGroup(this.activeGroup);
          }
        });
      }
      // this.groupService.editProjectRouteStations(this.activeRouteStation.groupId, [], [this.activeRouteStation.id])
      //   .subscribe((response) => {
      //     if (this.activeRouteStation) {
      //       this.groupService.editProjectRouteStations(this.selectedGroup.id ? +(this.selectedGroup.id) : 0,
      //           [this.activeRouteStation.id], [])
      //         .subscribe((response) => {
      //           this.alertService.alert(`Deine Station wurde der Gruppe ${this.selectedGroup} zugeordnet.`, AlertTypes.success);
      //           this.groupService.getProjectRouteStations(this.selectedGroup.id ? +(this.selectedGroup.id) : 0).subscribe(routeStations => {
      //             this.projectDataService.updateProjectRouteStations(routeStations);
      //           });
      //         });
      //     }
      //   });
    }
  }

  public navigateToSchema() {
    if (this.activeRouteStation) {
      sessionStorage.setItem('activeRouteStation', String(this.activeRouteStation.samId));
      sessionStorage.setItem('activeGroup', String(this.activeRouteStation.groupId));
    }

    if (this.activeGroup) sessionStorage.setItem('activeProject', String(this.activeGroup.projectId));

    this._router.navigate(['/project/schema']);

  }

  public onSearchInputChanged(event: string): void {
    this.searchInput = event;
    this.filterGroupsNew();

  }
/* 
  public filterGroups(): void {
    if (this.routerUrl === '/device/settings')
      this.filteredGroups = this.allNotArchivedProjectGroups.filter(group =>
        group.name.toLowerCase().includes(this.searchInput.toLowerCase()) || (group.projectName?.toLowerCase().includes(this.searchInput.toLowerCase()) ?? false)
      );
    if (this.routerUrl === '/project/settings')
      this.filteredGroups = this.allProjectGroups.filter(group =>
        group.name.toLowerCase().includes(this.searchInput.toLowerCase())
      );

  } */

  newFilteredGroups: ProjectGroup[] = [];

  public filterGroupsNew(): void {
    this.newFilteredGroups = [];
    /* TODO: Implement new filter logic */
/*     console.log(this.allProjects)
 */


    let filteredProjects = this.allProjects.filter(proj => proj.isActive && !proj.isDeleted);

    filteredProjects.forEach(proj => {
      proj.groups.forEach(g => {
        if (g.isArchived != true && g.isDeleted != true) {
          this.newFilteredGroups.push(g);
        }
      });
    });
/*     console.log(this.newFilteredGroups)
 */
    
  }

  public onCopyCoordinatesLinkBtnClick(): void {
    let latitude = this.activeRouteStation?.latitude;
    let longitude = this.activeRouteStation?.longitude;

    this.positionGmapService.copyCoordinatesLink(latitude as number, longitude as number)
  }



}
